// Anonymous, consent-free tracking utilities (DSGVO-konform)
// - Keine IP-Speicherung, keine Cookies, keine Fingerprints, keine User-IDs
// - Nur aggregierbare Metadaten: Pfad, Land (aus Domain), Sprache, Referrer-Host, UTM

import { supabase } from "@/integrations/supabase/client";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const STORAGE_KEY = "lov_utm";
const SESSION_PV_KEY = "lov_pv_session";

/** Land aus Domain ableiten, mit Browser-Sprache als Fallback. */
export function detectCountry(): string {
  if (typeof window === "undefined") return "UNKNOWN";
  const host = window.location.hostname.toLowerCase();
  if (host.endsWith(".at") || host.includes("4-tagewoche.at")) return "AT";
  if (host.endsWith(".ch") || host.includes("4-tagewoche.ch")) return "CH";
  if (host.endsWith(".de")) return "DE";
  if (host.includes("4tagewoche.org") || host.includes("banking.4tagewoche")) return "DE";
  // Fallback: Browser-Sprache
  const lang = (navigator.language || "").toLowerCase();
  if (lang.startsWith("de-at")) return "AT";
  if (lang.startsWith("de-ch") || lang.startsWith("fr-ch") || lang.startsWith("it-ch")) return "CH";
  if (lang.startsWith("de")) return "DE";
  if (lang.startsWith("fr")) return "FR";
  return "OTHER";
}

export function getLanguage(): string {
  if (typeof navigator === "undefined") return "unknown";
  return (navigator.language || "unknown").slice(0, 5);
}

export function getReferrerHost(): string | null {
  if (typeof document === "undefined" || !document.referrer) return null;
  try {
    const url = new URL(document.referrer);
    if (url.hostname === window.location.hostname) return null; // interner Wechsel
    return url.hostname;
  } catch {
    return null;
  }
}

/** UTM aus URL extrahieren und in sessionStorage persistieren (für späteren Lead-Submit). */
export function captureUtm(): UtmParams {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const fresh: UtmParams = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) fresh[key] = v.slice(0, 200);
  }
  if (Object.keys(fresh).length > 0) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
    } catch {}
    return fresh;
  }
  // Sonst aus Storage laden
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as UtmParams;
  } catch {}
  return {};
}

export function getStoredUtm(): UtmParams {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as UtmParams;
  } catch {}
  return {};
}

/** Zusatz-Metadaten zum Anhängen an Lead-Inserts. */
export function getLeadMeta() {
  const utm = getStoredUtm();
  return {
    country: detectCountry(),
    referrer_host: getReferrerHost(),
    utm_source: utm.utm_source ?? null,
    utm_medium: utm.utm_medium ?? null,
    utm_campaign: utm.utm_campaign ?? null,
    utm_term: utm.utm_term ?? null,
    utm_content: utm.utm_content ?? null,
  };
}

/** Pageview erfassen — pro Pfad max. 1× pro Session, um Spam zu vermeiden. */
export async function trackPageview(path: string) {
  if (typeof window === "undefined") return;
  // Dedupe pro Session+Pfad
  try {
    const seen = JSON.parse(sessionStorage.getItem(SESSION_PV_KEY) || "[]") as string[];
    if (seen.includes(path)) return;
    seen.push(path);
    sessionStorage.setItem(SESSION_PV_KEY, JSON.stringify(seen));
  } catch {}

  const utm = captureUtm();
  const payload = {
    path: path.slice(0, 500),
    country: detectCountry(),
    language: getLanguage(),
    referrer_host: getReferrerHost(),
    utm_source: utm.utm_source ?? null,
    utm_medium: utm.utm_medium ?? null,
    utm_campaign: utm.utm_campaign ?? null,
    utm_term: utm.utm_term ?? null,
    utm_content: utm.utm_content ?? null,
  };
  try {
    await supabase.from("page_views").insert(payload);
  } catch {
    // still
  }
}
